// Ported from vlookup-dramatic/app/events/events-browser.tsx by scripts/revamp/port-design.js
//
// QA round 2 Sep 2026 (testing sheet row: "The landing page is different from
// what we worked on in the dev site"): restored the design's single-page
// structure — no Upcoming/Past switch, one grid with the upcoming event(s)
// leading (full colour, RSVP) and the past events following (archive grey).
// /event/upcoming and /event/past both render it; /event/past (still in the
// sitemap, no longer linked in-app) scrolls to the grid, which is where the
// archive now lives. Data: the same GET all_events/{type} the old page
// called, both lists fetched here directly (the shared eventsData store slot
// can only hold one list at a time).
import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Image from "../../components/image";
import { ArrowUpRight, Calendar, Clock, MapPin } from "../../lib/icons";
import { eventsPageV3 } from "../../lib/content";
import { cn } from "../../lib/utils";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";

const DETAIL = (slug) => `/event-detail/${slug}`;
const FALLBACK_IMG = "/revamp/images/d-event-art.webp";

// GET all_events/{type} item -> card shape used by the design
const eventFromApi = (e) => ({
  slug: e.slug,
  title: e.title,
  time: e.full_date_view || e.addevent_description || "",
  location: e.address || "",
  img: e.image || FALLBACK_IMG,
});

export function EventsBrowser({ type = "upcoming" }) {
  const { pathname, search } = useLocation();
  const listingFrom = pathname + (search || "");
  const [upcoming, setUpcoming] = useState(null); // null = loading
  const [past, setPast] = useState(null);
  const pastRef = useRef(null);
  const scrolledToPast = useRef(false);

  useEffect(() => {
    let alive = true;
    agent
      .get(API.LOAD_EVENT + "/upcoming")
      .then((res) => alive && setUpcoming((res.data.data || []).map(eventFromApi)))
      .catch(() => alive && setUpcoming([]));
    agent
      .get(API.LOAD_EVENT + "/past")
      .then((res) => alive && setPast((res.data.data || []).map(eventFromApi)))
      .catch(() => alive && setPast([]));
    return () => {
      alive = false;
    };
  }, []);

  // The old /event/past address still works: it lands on the events grid.
  useEffect(() => {
    if (type === "past" && past && past.length && pastRef.current && !scrolledToPast.current) {
      scrolledToPast.current = true;
      pastRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [type, past]);

  const loading = upcoming === null || past === null;
  const [headFirst, ...headRest] = eventsPageV3.heading.split(" ");

  return (
    <div className="overflow-x-clip">
      {/* ===================== MASTHEAD ===================== */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute -right-32 -top-40 -z-10 size-[28rem] rounded-full bg-brand-blue/15 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -left-40 top-24 -z-10 size-96 rounded-full bg-brand-blue/15 blur-3xl" />

        <div className="wrap relative pb-16 pt-14 md:pb-20 md:pt-20">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-brand-red">
            Conversations, connections &amp; community
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-5xl font-semibold leading-[0.98] text-heading md:text-7xl">
            {headFirst}{" "}
            <em className="font-normal italic text-brand-red">{headRest.join(" ")}</em>
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">{eventsPageV3.sub}</p>

          {loading ? (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="overflow-hidden rounded-3xl border border-brand-navy/10 bg-card shadow-sm">
                  <div className="aspect-[16/10] animate-pulse bg-cream-deep" />
                  <div className="space-y-3 p-6">
                    <div className="h-5 w-3/4 animate-pulse rounded bg-cream-deep" />
                    <div className="h-4 w-1/2 animate-pulse rounded bg-cream-deep" />
                    <div className="h-4 w-2/3 animate-pulse rounded bg-cream-deep" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* One grid, exactly as the design: the upcoming event(s) lead in
                  full colour and the past events follow in the same flow. No
                  heading or count between them — that split was what made this
                  page read differently from the dev site. */}
              <div
                ref={pastRef}
                id="past-events"
                className="mt-10 grid scroll-mt-28 gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {upcoming.map((ev, i) => (
                  <EventCard key={ev.slug || `u${i}`} event={ev} upcoming index={i} from={listingFrom} />
                ))}
                {past.map((ev, i) => (
                  <EventCard key={ev.slug || `p${i}`} event={ev} index={upcoming.length + i} from={listingFrom} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <style>{`
        @keyframes evPageCardIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ev-card-in { animation: evPageCardIn 0.5s ease backwards; }
        @media (prefers-reduced-motion: reduce) {
          .ev-card-in { animation: none; }
        }
      `}</style>
    </div>
  );
}

/* ---------- local pieces ---------- */

// One card for every event (design markup). Upcoming and past differ only in
// the badge and the action: RSVP (on the detail page) versus the disabled
// "Event ended" state + Read more. Links carry state.from so the detail page's
// back control returns exactly here.
function EventCard({ event, upcoming = false, index = 0, from }) {
  const detail = { pathname: DETAIL(event.slug), state: { from } };
  return (
    <article
      style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
      className={cn(
        "ev-card-in group flex flex-col overflow-hidden rounded-3xl border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-35px_rgba(26,58,92,0.5)]",
        upcoming ? "border-brand-red/30" : "border-brand-navy/10"
      )}
    >
      <RouterLink to={detail} className="relative block aspect-[16/10] overflow-hidden bg-cream-deep" aria-label={event.title}>
        <Image
          src={event.img}
          alt={event.title}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 380px"
          className={cn(
            "object-cover transition-all duration-500 group-hover:scale-105",
            // Past events read as archive; the upcoming ones stay full colour.
            !upcoming && "grayscale-[0.35] group-hover:grayscale-0"
          )}
        />
        <span
          className={cn(
            "absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] backdrop-blur",
            upcoming ? "bg-brand-red text-white" : "bg-brand-ink/80 text-cream"
          )}
        >
          {upcoming && <Calendar className="size-3" />}
          {upcoming ? "Upcoming" : "Past event"}
        </span>
      </RouterLink>

      <div className="flex flex-1 flex-col gap-3 p-6">
        <h3 className="font-display text-xl font-semibold leading-snug text-heading">
          <RouterLink to={detail} className="transition-colors hover:text-brand-red">
            {event.title}
          </RouterLink>
        </h3>
        <div className="flex flex-col gap-2">
          {event.time && <MetaRow small icon={<Clock className="size-4" />} text={event.time} />}
          {event.location && <MetaRow small icon={<MapPin className="size-4" />} text={event.location} />}
        </div>

        {upcoming ? (
          <RouterLink
            to={detail}
            className="group/rsvp mt-auto inline-flex h-auto w-full items-center justify-center gap-2 rounded-full bg-brand-red px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-red-dark"
          >
            {eventsPageV3.rsvp.label}
            <ArrowUpRight className="size-4 transition-transform group-hover/rsvp:translate-x-0.5 group-hover/rsvp:-translate-y-0.5" />
          </RouterLink>
        ) : (
          <div className="mt-auto flex flex-col gap-2">
            <button
              type="button"
              disabled
              title="This event has ended"
              className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full border border-brand-navy/10 bg-brand-navy/5 px-5 py-2.5 text-sm font-bold text-brand-navy/35"
            >
              Event ended
            </button>
            <RouterLink to={detail} className="inline-flex items-center justify-center gap-1 text-sm font-bold text-brand-navy transition-colors hover:text-brand-red">
              Read more
              <ArrowUpRight className="size-3.5" />
            </RouterLink>
          </div>
        )}
      </div>
    </article>
  );
}

function MetaRow({ icon, text, small = false }) {
  return (
    <div className={cn("flex items-start gap-2.5 font-semibold text-brand-navy/80", small ? "text-[13px]" : "text-sm")}>
      <span className="mt-0.5 shrink-0 text-brand-red">{icon}</span>
      <span className="leading-relaxed">{text}</span>
    </div>
  );
}
